import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/useI18n';
import { IconClose } from './icons';

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * The whole of one row, in a panel that slides in from the right.
 *
 * A drawer rather than a modal for the same reason the reference build uses
 * one: what it shows is a **thing on the screen behind it**, and the list it
 * came from stays visible beside it. Somebody comparing two rows opens one,
 * reads, closes, opens the next — with a modal that is a page that keeps
 * disappearing.
 *
 * The keyboard contract is the modal's, unchanged: focus moves in on open, Tab
 * stays inside, Escape closes, and focus returns to whatever opened it. A panel
 * that traps a mouse and loses a keyboard is not a smaller dialog, it is a
 * broken one.
 */
export function Drawer({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useI18n();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const subtitleId = useId();

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const dialog = dialogRef.current;

    const firstControl = dialog?.querySelector<HTMLElement>(FOCUSABLE);
    if (firstControl !== null && firstControl !== undefined) {
      firstControl.focus();
    } else {
      dialog?.focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || dialog === null) return;

      const focusable = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      opener?.focus();
    };
  }, [onClose]);

  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <aside
          className="drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          {...(subtitle === undefined ? {} : { 'aria-describedby': subtitleId })}
          ref={dialogRef}
          tabIndex={-1}
        >
          <div className="drawer-head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 className="dt" id={titleId}>
                {title}
              </h2>
              {subtitle !== undefined && (
                <p className="dsub" id={subtitleId}>
                  {subtitle}
                </p>
              )}
            </div>
            <button type="button" className="icon-btn" onClick={onClose} aria-label={t('common.close')}>
              <IconClose />
            </button>
          </div>
          <div className="drawer-body">{children}</div>
          {footer !== undefined && <div className="drawer-foot">{footer}</div>}
      </aside>
    </>,
    document.body,
  );
}
